import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import Logo from "assets/images/logos/logo.png";
import { login, register } from "../../api/user"; // Import the register function
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const FullHeightGrid = styled(Grid)({
  height: "100vh",
});

const LoginContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "2rem",
  backgroundColor: "white",
  height: "100%",
});

const FullSizeImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const LogoImage = styled("img")({
  width: "150px", // Adjust the width as needed
  marginBottom: "1.5rem", // Space between logo and title
});

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [email, setEmail] = useState(""); // State for email
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false); // State to toggle between login and register
  const [verificationMessage, setVerificationMessage] = useState(""); // State for verification message
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (confirmPassword && newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    if (password && newConfirmPassword !== password) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isRegistering && password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    setIsLoading(true);

    try {
      if (isRegistering) {
        const credentials = { username, password, email };
        await register(credentials); // Call the register function
        setVerificationMessage(
          "Verification link sent to your email address. Please verify it within 15 minutes."
        );
        setIsRegistering(false); // Switch back to login form
      } else {
        const credentials = { username, password };
        const response = await login(credentials); // Call the login function

        // Check the user's role and navigate accordingly
        const role = response.data.role;
        if (role === 1 || role === 2) {
          navigate("/admin/portal");
        } else if (role === 3) {
          if (response.data.hasStudents === false) {
            navigate("/parents/portal?tab=student-details");
          } else {
            navigate("/parents/portal");
          }
        }
      }
    } catch (error) {
      console.error("Error:", error);
      // Additional error handling if required
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FullHeightGrid container>
      <Grid item xs={12} md={6}>
        <LoginContainer>
          <LogoImage src={Logo} alt="Alsaraya Logo" />
          <Typography component="h1" variant="h4" sx={{ mb: 4, color: "gray" }}>
            Alsaraya Portal
          </Typography>
          {verificationMessage && (
            <Typography component="p" variant="body1" sx={{ mb: 2, color: "red" }}>
              {verificationMessage}
            </Typography>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: "400px" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {isRegistering && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={!!passwordError}
                  helperText={passwordError}
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ py: 1.5 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isRegistering ? (
                "Register"
              ) : (
                "Sign In"
              )}
            </Button>
            <Button
              fullWidth
              variant="text"
              size="large"
              onClick={() => setIsRegistering((prev) => !prev)}
              sx={{ mt: 2, textDecoration: "underline" }}
            >
              {isRegistering ? "Back to Sign In" : "Student Register"}
            </Button>
          </Box>
        </LoginContainer>
      </Grid>
      <Grid item xs={12} md={6} sx={{ height: "100%" }}>
        <FullSizeImage
          src="https://img.freepik.com/free-vector/green-gradient-background-gradient-3d-design_343694-3667.jpg"
          alt="Login background"
        />
      </Grid>
    </FullHeightGrid>
  );
};

export default LoginPage;
